import { h, Fragment } from 'preact';
import { useEffect, useState,useRef } from 'preact/hooks';

const Panel = ({
  childrenVisible,
  children,
  alignment = 'right',
  context = null,
  triggers,
  defaultFocus,
  defaultOpen = false,
  additionalClasses = '',
  overlay = false,
  overlayId = 'overlay',
  overlayOpacity = 'opacity-25',
}) => {
  const { openTriggerEl, closeTriggerEl, toggleEl, openTriggerElArray } = 
    triggers;
  const closeTriggers = 
    Array.isArray(closeTriggerEl) ? closeTriggerEl : [closeTriggerEl];
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const [lastOpenTriggerEl,setLastOpenTriggerEl] = useState(null)
  const panelRef = useRef(null);
  const firstFocusableElement = useRef(null);
  const lastFocusableElement = useRef(null);
  useEffect(() => {
    if (isOpen && openTriggerEl) {  
        setLastOpenTriggerEl(openTriggerEl);
    }
  }, [isOpen, openTriggerEl]);

  const handleOpenEvent = (e) => {
    if (isOpen) {
      return;
    }
    if (e.target instanceof Element && 
      (e.target.closest('.increase-button') || 
      e.target.closest('.decrease-button'))) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(true);
    document.body.classList.add('overflow-hidden');
    
    if (typeof childrenVisible === 'function') {
      childrenVisible(true)
    }
    setTimeout(() => {
      if (panelRef.current) {
        const focusableElements = panelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          // eslint-disable-next-line prefer-destructuring
          firstFocusableElement.current = focusableElements[0];
          // eslint-disable-next-line max-len
          lastFocusableElement.current = focusableElements[focusableElements.length - 1];
        }
      }
    }, 0);

    if (lastOpenTriggerEl && typeof lastOpenTriggerEl.focus === 'function') {
      lastOpenTriggerEl.focus();
    } 

    if (e.isTrusted && openTriggerEl !== undefined) {
      setLastOpenTriggerEl(openTriggerEl);
    }

    if (e.isTrusted 
        && openTriggerEl === undefined 
        && e.srcElement?.hasAttribute('data-tray')) {
      setLastOpenTriggerEl(e.srcElement);
    }
    if(!e.isTrusted && e.srcElement.activeElement.hasAttribute('data-product-form-btn') ||
     !e.isTrusted && e.srcElement.activeElement.hasAttribute('data-engraving-button') ) {
      setLastOpenTriggerEl(e.srcElement.activeElement)
    }
    if (closeTriggerEl) {
      const element = Array.isArray(closeTriggerEl) ? 
        closeTriggerEl[0] : 
        closeTriggerEl;
      if (element && e.isTrusted || element && !e.isTrusted 
          && e.srcElement.activeElement.hasAttribute('data-product-form-btn') || 
          element && !e.isTrusted &&
           e.srcElement.activeElement.hasAttribute('data-engraving-button')) {

        element.tabIndex = 0;
        element.focus();
      } 
    }

    setTimeout(() => {
      if (firstFocusableElement.current) {
        firstFocusableElement.current.focus();
      }
    }, 0); 
    
  };

  useEffect(() => {
    if (isOpen && defaultFocus && typeof defaultFocus === 'string') {
      const focusElement = document.querySelector(defaultFocus);
      if (focusElement) {
        focusElement.focus();
      }
    }
  }, [isOpen, defaultFocus]);

  const handleCloseEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(false);
    document.body.classList.remove('overflow-hidden');

    if (lastOpenTriggerEl && typeof lastOpenTriggerEl.focus === 'function') {
      lastOpenTriggerEl.focus();
    } 

    if (lastOpenTriggerEl && typeof lastOpenTriggerEl.focus === 'function') {
      lastOpenTriggerEl.focus();
    } 

    if (typeof childrenVisible === 'function') {
      childrenVisible(false)
    }

    if (closeTriggerEl) {
      const element = Array.isArray(closeTriggerEl) ? 
        closeTriggerEl[0] : 
        closeTriggerEl;
      if (element) {
        element.tabIndex = -1;
      }
    }
  };
  const handleToggleEvent = () => setIsOpen(!isOpen);

  useEffect(() => {
    if (openTriggerEl) openTriggerEl.addEventListener('click', handleOpenEvent);
    if ( openTriggerElArray )
      openTriggerElArray.forEach((trigger) =>
        trigger.addEventListener('click', handleOpenEvent),
      );
    if (closeTriggers)
      closeTriggers.forEach(el => el && el.addEventListener('click', handleCloseEvent)); 
    if (toggleEl) toggleEl.addEventListener('click', handleToggleEvent);
    if (context) {
      document.addEventListener(`${context}:open`, handleOpenEvent);
      document.addEventListener(`${context}:close`, handleCloseEvent);
    }

    return () => {
      if (openTriggerEl)
        openTriggerEl.removeEventListener('click', handleOpenEvent);
      if (openTriggerElArray)
        openTriggerElArray.forEach((trigger) =>
          trigger.removeEventListener('click', handleOpenEvent),
        );
      if (closeTriggers)
        closeTriggers.forEach(el => el && el.removeEventListener('click', handleCloseEvent));
      if (toggleEl) toggleEl.removeEventListener('click', handleToggleEvent);
      if (context) {
        document.removeEventListener(`${context}:open`, handleOpenEvent);
        document.removeEventListener(`${context}:close`, handleCloseEvent);
      }
    };
  }, [openTriggerEl, closeTriggers, toggleEl, openTriggerElArray]);

  const closedClass =
    alignment === 'right' ? 'translate-x-full' : '-translate-x-full';

  
  useEffect(() => {
    const appendOverlay = () => {
      const overlayElement = document.createElement('div');
      overlayElement.id = overlayId; 
      overlayElement.className = 
      `block w-full h-full fixed inset-0 z-[99998] bg-black ${overlayOpacity}`;
      overlayElement.onclick = handleCloseEvent; 

      document.body.appendChild(overlayElement);
    };

    const removeOverlay = () => {
      const overlayElement = document.getElementById(overlayId);
      if (overlayElement) {
        document.body.removeChild(overlayElement);
      }
    };

    if (isOpen && overlay === true) {
      appendOverlay();
    } else {
      removeOverlay();
    }

    return () => removeOverlay();
  }, [isOpen]); 

  const handleKeyDown = (e) => {
    if (e.key === 'Tab' && panelRef.current) {
      // eslint-disable-next-line max-len
      if (e.shiftKey && document.activeElement === firstFocusableElement.current) {
        e.preventDefault();
        lastFocusableElement.current.focus();
      // eslint-disable-next-line max-len
      } else if (!e.shiftKey && document.activeElement === lastFocusableElement.current) {
        e.preventDefault();
        firstFocusableElement.current.focus();
      }
    }
  };

  return (
    <>
      <div
       ref={panelRef}
       onKeyDown={handleKeyDown}
        className={`
          block
          w-full h-full
          fixed
          top-0
          bottom-0
          transition-transform
          z-[99999]
          bg-white
          w-[375px]
          duration-500
          ${alignment === 'right' ? 'right-0' : 'left-0'} ${
          isOpen ? 'translate-x-0 ' : closedClass
        } ${additionalClasses}`}
      >
        
      {children}
      </div>
    </>
  );
};

export default Panel;
